import { createContext, useContext, useState, ReactNode } from "react";
import { Course } from "@/data/mockCourses";

export interface CartItem {
  course: Course;
  addedAt: Date;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (course: Course) => void;
  removeFromCart: (courseId: string) => void;
  clearCart: () => void;
  isInCart: (courseId: string) => boolean;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);

  const addToCart = (course: Course) => {
    if (!isInCart(course.id)) {
      setItems((prev) => [...prev, { course, addedAt: new Date() }]);
    }
  };

  const removeFromCart = (courseId: string) => {
    setItems((prev) => prev.filter((item) => item.course.id !== courseId));
  };

  const clearCart = () => {
    setItems([]);
  };

  const isInCart = (courseId: string) => {
    return items.some((item) => item.course.id === courseId);
  };

  const totalItems = items.length;

  const totalPrice = items.reduce((sum, item) => sum + item.course.price, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        clearCart,
        isInCart,
        totalItems,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
